import type { KyInstance, Options as KyOptions } from 'ky';
import ky, { HTTPError } from 'ky';
import { getGlobalLogger } from '@zg-rentals/logger-base';
import { getGlobalMonitor } from '@zg-rentals/monitor-base';
import { logError } from '@zg-rentals/log-error';
import { makeModuleInstance } from '@zg-rentals/util';
import { memoize } from 'es-toolkit';

export type Client = KyInstance;
export type Options = KyOptions;
export { HTTPError };

const StartTime = Symbol('StartTime');

const Monitoring = Symbol('Monitoring');

export const getClientOptions = (client: Client) =>
  ({
    monitoring: (client as any)[Monitoring] !== false, // eslint-disable-line @typescript-eslint/no-explicit-any
  }) as const;

/**
 * Create a new HTTP client instance with full control over configuration.
 *
 * @note typically you want to use `getClient().extend({...overrides})` to simply
 * override the default configuration value(s) you need to override
 *
 * @note if you are looking to configure code generated clients, import `getClient` from
 * `@zg-rentals/ts-client-http-client`
 */
export const createClient = ({
  logging,
  monitoring,
  ...options
}: KyOptions & {
  logging?: boolean;
  monitoring?: boolean;
} = {}): Client => {
  const logger = memoize(() => (logging ? getGlobalLogger('http-client') : undefined));
  const monitor = memoize(() => (monitoring ? getGlobalMonitor() : undefined));

  const client = ky
    .create({
      hooks: {
        beforeRequest: [
          (request, requestOptions) => {
            monitor()?.count({ name: 'httpClient beforeRequest volume total', amount: 1 });

            monitor()?.count({
              name: `httpClient beforeRequest volume method ${request.method} total`,
              amount: 1,
            });

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (requestOptions as any)[StartTime] = Date.now();

            logger()?.debug(
              {
                url: request.url,
                method: request.method,
              },
              'beforeRequest',
            );
          },
        ],

        afterResponse: [
          (request, requestOptions, response) => {
            monitor()?.count({ name: 'httpClient afterResponse volume total', amount: 1 });

            monitor()?.count({
              name: `httpClient afterResponse volume method ${request.method} total`,
              amount: 1,
            });

            monitor()?.count({
              name: `httpClient afterResponse volume statusCode ${response.status} total`,
              amount: 1,
            });

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const latencyMs = (Date.now() - (requestOptions as any)[StartTime]) as number;

            monitor()?.gauges({
              name: 'httpClient afterResponse latencyMs',
              amount: latencyMs,
            });

            monitor()?.gauges({
              name: `httpClient afterResponse latencyMs method ${request.method}`,
              amount: latencyMs,
            });

            if (latencyMs >= 30 * 1000) {
              logger()?.warn(
                {
                  url: request.url,
                  method: request.method,
                  status: response.status,
                  latencyMs,
                },
                'afterResponse slow request',
              );
            } else {
              logger()?.debug(
                {
                  url: request.url,
                  method: request.method,
                  status: response.status,
                  latencyMs,
                },
                'afterResponse',
              );
            }
          },
        ],

        beforeError: [
          (error) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const latencyMs = (Date.now() - (error.options as any)[StartTime]) as number;

            monitor()?.count({ name: 'httpClient beforeError volume total', amount: 1 });

            monitor()?.count({
              name: `httpClient beforeError volume method ${error.request?.method ?? 'UNKNOWN'} total`,
              amount: 1,
            });

            monitor()?.count({
              name: `httpClient beforeError volume statusCode ${error.response?.status ?? 'UNKNOWN'} total`,
              amount: 1,
            });

            monitor()?.gauges({
              name: 'httpClient beforeError latencyMs',
              amount: latencyMs,
            });

            monitor()?.gauges({
              name: `httpClient beforeError latencyMs method ${error.request?.method ?? 'UNKNOWN'} total`,
              amount: latencyMs,
            });

            if (logging) {
              logError({
                error,
                context: {
                  url: error.request.url,
                  method: error.request.method,
                  status: error.response.status,
                  latencyMs,
                  traceparent: error.response.headers.get('traceparent'),
                  eid: error.response.headers.get('x-eid'),
                  xPxBlocked: error.response.headers.get('x-px-blocked'),
                },
                errorType: 'HTTPError',
              });
            }

            return error;
          },
        ],
      },
    })
    .extend(options);

  // Note: this is an annoying hack, but allows us to scope monitoring/logging to the client, allowing
  // us to conditionally emit metrics in the codegen packages. This is necessary because we use the codegen
  // packages to emit metrics, so there is a recursive loop we need to avoid.
  (client as any)[Monitoring] = monitoring === true; // eslint-disable-line @typescript-eslint/no-explicit-any

  const extend = client.extend;
  client.extend = function (...args) {
    const extended = extend.call(this, ...args);
    (extended as any)[Monitoring] = monitoring === true; // eslint-disable-line @typescript-eslint/no-explicit-any
    return extended;
  };

  return client;
};

export const { get: getClient, set: setClient } = makeModuleInstance(createClient({ logging: true, monitoring: true }));
